/**
 * Section 12b — Edge AI (Pragya).
 *
 * The self-learning next-day/next-week symbol picker: captures indicator data
 * points across timeframes, labels the forward move, learns which conditions
 * precede good moves, ranks candidates, tracks its record, and paper-trades.
 * Documents the page's tabs + the bootstrap flow. Suggest-only / paper.
 */
import React from 'react';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const H3 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2.5, mb: 0.75 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);
const LI = ({ children }: { children: React.ReactNode }) => (
  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}><ListItemText primary={children} /></ListItem>
);

const Section_edge_ai: GuideSection = {
  id: 'edge-ai',
  label: 'Edge AI (Pragya)',
  group: 'pages',
  icon: <AutoAwesomeIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Edge AI · Pragya</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        A self-learning engine that captures your indicators across timeframes, learns which combinations
        precede good moves, and scores the symbols most likely to move (up or down) <strong>next day / next
        week</strong>. It is <strong>suggest-only</strong> — it never places live orders. It now lives on
        the <OpenInApp to="/ai-desk?tab=engine" label="AI Desk → Engine" /> tab rather than on a page of
        its own.
      </Typography>

      <GuideCallout kind="warning" title="Its ranked board is no longer shown, and the reason is a number">
        Over <strong>1,492 resolved picks</strong> the board&apos;s directional accuracy was
        <strong> 18%</strong> against its own baseline of <strong>17%</strong> — an edge of
        <strong> +0.8 percentage points</strong>, inside the standard error at that sample — with
        <strong> 67%</strong> of picks producing no move at all and no real trade ever taken on one.
        So the ranked list stopped being presented as something to act on. Nothing was deleted: the
        capture, labelling and retraining pipeline still runs, the corpus still grows, and the model
        health that would justify bringing it back is on the Engine tab. The two parts that DID earn
        their place moved to where they are useful — the <strong>forward-range bands</strong> to the
        AI Desk&apos;s Options tab, beside the strike and expiry decisions they inform, and
        <strong> per-symbol reliability</strong> to the foot of its Performance tab.
      </GuideCallout>

      <GuideCallout kind="info" title="Off by default — you opt in">
        Edge AI does nothing until you turn it ON. Once enabled it auto-captures, labels,
        learns (~daily) and predicts every cycle. Auto-Trade is a separate opt-in and is <strong>paper only</strong>.
      </GuideCallout>

      <H id="how">How it works — the pipeline</H>
      <P>Each step feeds the next, end to end:</P>
      <List dense sx={{ pl: 1 }}>
        <LI><><strong>Capture</strong> — for every symbol in your universe, it records all 14 OverView
          constituents (EMA, Avg-Cloud, CPR, C-POC, H-POC, Rel-Strength, Chg%, MACD, JHM, WR%, BO-D, BO-W,
          Volume, AV-Band) across <strong>1h / 4h / 1D / 1W</strong>, plus the composite score, zone, ATR%,
          RSI, volume z-score, recent returns and a market-regime tag — a fixed feature vector per snapshot.</></LI>
        <LI><><strong>Label</strong> — once a snapshot's horizon elapses, it records the forward move
          (close-to-close return + best/worst excursion) as <strong>GOOD_UP / GOOD_DOWN / NEUTRAL</strong>{' '}
          using a <strong>volatility-relative</strong> "good move" threshold: the bar a move must clear is scaled
          by the symbol's own ATR% (so the same 1.5% is a yawn for a high-vol coin but a real event for a low-vol
          large-cap). The configured % is only a fallback when ATR can't be computed.</></LI>
        <LI><><strong>Learn</strong> — per horizon it trains an interpretable Naive-Bayes model and a logistic
          regression, validates them <em>purged walk-forward</em> (train / calibrate / test split with a gap that
          drops rows whose forward windows overlap the next fold — no horizon leakage), and keeps whichever
          (or a blend) performs best. The held-out report includes <strong>calibration (ECE / Brier)</strong>{' '}
          alongside AUC and lift. Models are versioned and only promoted when they beat the base rate and the
          previous model — so the logic <strong>auto-upgrades</strong> as data accrues.</></LI>
        <LI><><strong>Rank</strong> — it scores every symbol's latest snapshot into the Board: direction,
          calibrated probability, expected move, and the conditions that drove the call.</></LI>
        <LI><><strong>Track + paper-trade</strong> — it records predicted-vs-actual and (optionally) opens
          simulated positions to show a real-time forward P&L.</></LI>
      </List>

      <H id="tabs">The tabs</H>
      <H3>Board — "tomorrow's candidates"</H3>
      <P>
        Ranked Up / Down cards: symbol, calibrated probability (with a confidence chip), expected move %, and
        the condition chips behind the call (in the OverView vocabulary, e.g. "Score (1D) up · HTF aligned").
        Toggle the horizon (Next day / Next week) at the top; filter by direction. A <strong>"Tradable only"</strong>{' '}
        toggle hides analysis-only feeds so the board is just symbols you can act on. Each card carries{' '}
        <strong>one-click actions</strong> — ★ favourite, open the chart to validate, and (for crypto) build an
        option basket — so a pick becomes an action without leaving the page.
      </P>
      <H3>Why &amp; Model</H3>
      <P>
        The learned logic, made readable: per-direction metrics (base rate, top-decile precision, lift, AUC),
        the active scorer (NB / LR / blend) with an AUC comparison, the top feature-importance bars, the mined
        plain-English rules ("Score (1D) = up → UP 71%, lift 1.4×"), and the version history. The version-history
        table now shows <strong>AUC and Lift (up/down) per version</strong> — watch them trend up across retrains
        to confirm the model is genuinely getting better as data accrues. <strong>Retrain now</strong> rebuilds
        immediately.
      </P>
      <H3>Track Record</H3>
      <P>
        What it predicted vs what happened: good-move hit-rate, direction accuracy, a cumulative "edge" curve
        (realized move in the predicted direction if you took every pick), and a recent predicted-vs-actual
        table. Tracks real forward picks only — it fills in as picks reach their horizon. At the top, a{' '}
        <strong>"Real vs paper"</strong> panel contrasts the engine's simulated paper P&L with the <strong>real
        ₹ P&L of trades you actually took on Edge's picks</strong> (see Analytics → scope "Edge AI") — the
        credible, real-money validation.
      </P>
      <H3>Auto-Trade (PAPER)</H3>
      <P>
        Opt-in, <strong>simulated</strong> trading of the top picks — no real orders. When ON it opens paper
        positions for the top-N picks above your probability threshold (at a configurable ₹ notional) and
        settles them at the horizon, showing open positions, closed P&L (% and ₹), win rate, and a paper
        equity curve.
      </P>
      <H3>Accuracy</H3>
      <P>Calibration of past picks — predicted probability vs actual hit-rate per band, overall hit-rate, and AUC.</P>
      <H3>Settings</H3>
      <P>
        Horizons, the "good move" % thresholds that define a positive label, board min-probability, the
        watchlist-based <strong>universe selector</strong>, opt-in alert routing (Dashboard / Telegram), and the
        data-pipeline buttons (Capture, Label, Retrain, Backfill).
      </P>

      <H id="range">Range predictor — forward price bands</H>
      <P>
        The <strong>Range</strong> tab forecasts <em>where BTC/ETH is likely to trade</em> over
        <strong> 1 day / 2 days / 3 days / 1 week / 1 month</strong> — not a direction, but a calibrated
        <strong> price band</strong>. It's the foundation for automatically sizing option baskets (iron condors,
        strangles) later, once it's predicting well.
      </P>
      <List dense sx={{ pl: 1 }}>
        <LI><><strong>What it shows</strong> — per symbol × horizon: the <strong>80% range</strong> (low–high the
          price should stay within ~80% of the time), the <strong>median</strong> (expected drift), the expected
          ± move, a <strong>Vol band</strong> baseline to compare against, and the model's <strong>coverage</strong>
          + sample count.</></LI>
        <LI><><strong>How it learns</strong> — quantile models (P10…P90) trained on the option-derived FNO stats
          (PCR, GEX, max-pain, ATM straddle, and IV where available) plus price technicals, validated on a
          purged walk-forward split. It <strong>retrains itself daily</strong> and sharpens as history accrues.</></LI>
        <LI><><strong>Reading coverage</strong> — coverage ≈ 80% means the band is well-calibrated; far below 80%
          (common on 1W/1M early on) means that horizon is still "cold" — trust the shorter horizons first. Compare
          the learned band to the Vol band to see the model's edge.</></LI>
        <LI><><strong>Rebuild now</strong> — forces an immediate capture + retrain + refresh (it also runs
          automatically each cycle).</></LI>
      </List>
      <GuideCallout kind="info" title="Suggest-only, and maturing">
        The range bands are a planning aid, not a guarantee. Short horizons calibrate first; give the 1-week /
        1-month bands a few weeks of data before relying on them — the coverage number tells you when each
        horizon is ready.
      </GuideCallout>

      <H id="start">Getting started</H>
      <List dense sx={{ pl: 1 }}>
        <LI>Toggle the engine <strong>ON</strong> (top-right).</LI>
        <LI><strong>Settings → Backfill history</strong> (e.g. 60 days) to seed snapshots + outcomes from your
          watchlists' candle history so the model isn't starting cold.</LI>
        <LI><strong>Retrain now</strong> — builds the first model and populates the Board.</LI>
        <LI>(Optional) open <strong>Auto-Trade → ON</strong> to start the paper-trading ledger.</LI>
      </List>
      <GuideCallout kind="tip">
        From then on it runs itself: captures + labels + retrains (~daily) + re-predicts each cycle. The
        universe defaults to all your strategy watchlists; narrow it in Settings.
      </GuideCallout>

      <GuideCallout kind="warning" title="It's a probability aid, not a guarantee">
        Predictions are calibrated estimates from historical patterns — they will be wrong a meaningful share
        of the time. Use the Track Record and Accuracy tabs to judge whether the current model is actually
        working before relying on it, and never treat a candidate as a trade instruction.
      </GuideCallout>

      <GuideExample title="A morning routine with Edge AI">
        Open Edge AI → Board, horizon "Next day". Top card: BTCUSD, UP, 64% (chips: Score (1D) up · HTF
        aligned · RelStr up · Vol up).<br />
        Why &amp; Model: scorer "blend", AUC 0.62, top-decile precision 58% vs 41% base rate (lift 1.4×) —
        a real but modest edge.<br />
        Track Record: cumulative edge curve rising over the last 3 weeks, direction accuracy 57%.<br />
        Decision: treat it as a watch-list tilt, confirm on the chart before acting. (Auto-Trade is paper, so
        you can also just let it log the hypothetical P&L.)
      </GuideExample>
    </>
  ),
  searchIndex: [
    { heading: 'Edge AI · Pragya', anchor: 'how', body: 'edge ai pragya self learning predict next day next week ranked candidates machine learning' },
    { heading: 'How it works', anchor: 'how', body: 'capture label learn rank features naive bayes logistic regression walk forward' },
    { heading: 'Board / Why / Track Record / Auto-Trade / Accuracy', anchor: 'tabs', body: 'board model why accuracy track record paper auto trade calibration importance rules tradable filter favourite actions auc lift version history real vs paper' },
    { heading: 'Range predictor — forward price bands', anchor: 'range', body: 'range predictor forward price band BTC ETH quantile 1 day week month iron condor strangle option basket coverage vol band' },
    { heading: 'Getting started with Edge AI', anchor: 'start', body: 'enable backfill retrain universe watchlist paper trade' },
  ],
};

export default Section_edge_ai;
